import { CameraOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, List, Slider, Space, Tag, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { useIntl } from 'react-intl';
import { createOcrPhotoCandidates, dedupeOcrCandidates } from '@/shared/camera/ocr-photo-candidates';
import type { ValueScanCandidate, ValueScanPurpose } from '@/shared/camera/value-scanner';
import { messages } from './messages';

interface OcrPhotoPickerProps {
  initialPhoto?: Blob;
  onAccept: (candidate: ValueScanCandidate) => void;
  purpose: ValueScanPurpose;
}

// A wide viewfinder box: meter values (serial, counter, unit) are short single-line
// strings, so the user zooms/pans until the value fills the box.
const CROP_ASPECT = 2.5;
const CROP_MAX_ZOOM = 5;

export function OcrPhotoPicker({ initialPhoto, onAccept, purpose }: OcrPhotoPickerProps) {
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [photoFile, setPhotoFile] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropAreaPixels, setCropAreaPixels] = useState<Area | null>(null);
  const [candidates, setCandidates] = useState<ValueScanCandidate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl],
  );

  useEffect(() => {
    if (initialPhoto) {
      handleFile(initialPhoto);
    }
  }, [initialPhoto]);

  function handleFile(file: Blob | undefined) {
    if (!file) {
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPhotoFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropAreaPixels(null);
    setCandidates([]);
    setError(null);
    setMessage(null);
  }

  async function scanCrop() {
    if (!photoFile) {
      return;
    }

    setCandidates([]);
    setError(null);
    setMessage(null);
    setIsScanning(true);

    try {
      const nextCandidates = await createOcrPhotoCandidates(photoFile, purpose, cropAreaPixels ?? undefined);
      const dedupedCandidates = dedupeOcrCandidates(nextCandidates);
      setCandidates(dedupedCandidates);
      setMessage(
        dedupedCandidates.length > 0
          ? intl.formatMessage(
              dedupedCandidates.length === 1
                ? messages.scannerPhotoCandidatesFoundOne
                : messages.scannerPhotoCandidatesFoundOther,
              { count: dedupedCandidates.length },
            )
          : intl.formatMessage(messages.scannerPhotoNoUsableText),
      );
    } catch (error) {
      setError(error instanceof Error ? error.message : intl.formatMessage(messages.scannerPhotoReadError));
    } finally {
      setIsScanning(false);
    }
  }

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <input
        accept="image/*"
        capture="environment"
        hidden
        onChange={event => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = '';
          handleFile(file);
        }}
        ref={fileInputRef}
        type="file"
      />

      <Button block icon={<CameraOutlined />} onClick={() => fileInputRef.current?.click()}>
        {intl.formatMessage(messages.scannerPhotoReadFromPhoto)}
      </Button>

      {previewUrl ? (
        <>
          <Typography.Text type="secondary">{intl.formatMessage(messages.scannerPhotoCropHint)}</Typography.Text>
          <div style={{ borderRadius: 8, height: 280, overflow: 'hidden', position: 'relative', width: '100%' }}>
            <Cropper
              aspect={CROP_ASPECT}
              crop={crop}
              image={previewUrl}
              maxZoom={CROP_MAX_ZOOM}
              mediaProps={{ alt: intl.formatMessage(messages.scannerPhotoSourceAlt) }}
              onCropChange={setCrop}
              onCropComplete={(_, croppedAreaPixels) => setCropAreaPixels(croppedAreaPixels)}
              onZoomChange={setZoom}
              showGrid={false}
              zoom={zoom}
            />
          </div>
          <Slider max={CROP_MAX_ZOOM} min={1} onChange={setZoom} step={0.1} tooltip={{ open: false }} value={zoom} />
          <Button block loading={isScanning} onClick={() => void scanCrop()} type="primary">
            {intl.formatMessage(messages.scannerPhotoReadValue)}
          </Button>
        </>
      ) : null}

      {error ? <Alert closable message={error} showIcon type="warning" /> : null}
      {message ? <Alert message={message} showIcon type={candidates.length > 0 ? 'success' : 'info'} /> : null}

      {candidates.length > 0 ? (
        <List
          dataSource={candidates}
          renderItem={candidate => (
            <List.Item
              actions={[
                <Button
                  key="use"
                  onClick={() => {
                    onAccept(candidate);
                    setCandidates([]);
                    setMessage(
                      intl.formatMessage(messages.scannerPhotoFilledValue, {
                        value: candidate.value,
                      }),
                    );
                  }}
                  type="link"
                >
                  {intl.formatMessage(messages.scannerActionsUse)}
                </Button>,
              ]}
            >
              <List.Item.Meta
                description={
                  <Flex gap="small" wrap>
                    <Tag color="green">{intl.formatMessage(messages.scannerCandidateText)}</Tag>
                    <Typography.Text type="secondary">{Math.round(candidate.confidence * 100)}%</Typography.Text>
                  </Flex>
                }
                title={candidate.value}
              />
            </List.Item>
          )}
          size="small"
        />
      ) : null}
    </Space>
  );
}
